import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { isValid, parse, parseISO } from "date-fns";
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { referenceService } from "src/api/references/referenceService";
import { PatientMonitoringResponseDto } from "src/api/references/referenceServicesDtos";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { formatDateTime, toLocalISO } from "src/utils/date/dateHelper";

type SpeechRecognitionLike = {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
  maxAlternatives?: number;
  onresult?: (event: any) => void;
  onstart?: () => void;
  onend?: () => void;
  onerror?: () => void;
  start: () => void;
  stop: () => void;
};

type MonitoringsPanelProps = {
  patientId?: string | number;
};

type MonitoringRow = {
  id: number;
  cuser: string;
  note: string;
  ptime: string;
  isDoctor: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

const parseMonitoringTimestamp = (value: string | null | undefined): number => {
  if (!value) return Number.NEGATIVE_INFINITY;

  const isoDate = parseISO(value);
  if (isValid(isoDate)) {
    return isoDate.getTime();
  }

  const localDate = parse(value, "dd.MM.yyyy HH:mm", new Date());
  if (isValid(localDate)) {
    return localDate.getTime();
  }

  const browserDate = new Date(value);
  const timestamp = browserDate.getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

function ActionIconButton({
  children,
  disabled,
  onClick,
}: Readonly<{ children: React.ReactNode; disabled?: boolean; onClick?: () => void }>) {
  return (
    <IconButton
      size="small"
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 28,
        height: 28,
        color: "#687D96",
      }}
    >
      {children}
    </IconButton>
  );
}

function RoleInfo({ isDoctor, user }: Readonly<{ isDoctor: boolean; user: string }>) {
  const { t } = useTranslation();
  const iconSrc = isDoctor
    ? "/assets/icons/pages/nurse/patient-data/icons/doctor.svg"
    : "/assets/icons/pages/nurse/detail-grid/nurse.svg";
  const roleLabel = isDoctor
    ? t("nurse.patient-table.monitorings.doctor")
    : t("nurse.patient-table.monitorings.nurse");

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Tooltip title={roleLabel} arrow>
        <Box component="img" src={iconSrc} alt={roleLabel} sx={{ width: 18, height: 18, objectFit: "contain" }} />
      </Tooltip>
      <Typography sx={{ color: "#687D96", fontSize: 14, fontFamily: "Poppins", fontWeight: 400, lineHeight: "21px" }}>
        {user}
      </Typography>
    </Stack>
  );
}

function MonitoringDraftCard({
  note,
  saving,
  editing,
  isRecording,
  onNoteChange,
  onSave,
  onMicClick,
}: Readonly<{
  note: string;
  saving: boolean;
  editing: boolean;
  isRecording: boolean;
  onNoteChange: (value: string) => void;
  onSave: () => void;
  onMicClick: () => void;
}>) {
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: 275,
        p: "5px 0",
        background: "#fff",
        boxShadow: "0px 1px 8px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        borderRadius: "8px",
      }}
    >
      <Stack alignItems="center" spacing={1}>
        <Box sx={{ alignSelf: "stretch", height: 44, px: 1.5, py: 1 }} />

        <Box
          sx={{
            width: "calc(100% - 24px)",
            p: 1.5,
            background: "#F1F4F8",
            borderRadius: "8px",
            outline: "1px solid #DBE2EB",
            outlineOffset: "-1px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Stack sx={{ width: "100%" }} spacing={1.8}>
            <Box
              onClick={saving ? undefined : onSave}
              sx={{
                width: 96,
                height: 29,
                px: 1.25,
                py: 0.5,
                background: "rgba(99, 180, 255, 0.20)",
                borderRadius: "4px",
                outline: "1px solid #2979C3",
                outlineOffset: "-1px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "#2979C3",
                cursor: saving ? "default" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? (
                <CircularProgress size={16} sx={{ color: "#2979C3" }} />
              ) : (
                <>
                  <CheckRoundedIcon sx={{ width: 16, height: 16 }} />
                  <Typography sx={{ fontSize: 13, fontFamily: "Roboto", fontWeight: 400, lineHeight: "22px", textTransform: "capitalize" }}>
                    {editing ? "Güncelle" : "Kaydet"}
                  </Typography>
                </>
              )}
            </Box>

            <Box
              sx={{
                minHeight: 137,
                position: "relative",
                borderRadius: "4px",
                outline: "1px solid #2196F3",
                outlineOffset: "-1px",
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <ByzMuiTextField
                value={note}
                onChange={(e) => onNoteChange(e.target.value)}
                multiline
                rows={5}
                placeholder="İzlem notunu giriniz"
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    alignItems: "stretch",
                    background: "#fff",
                    minHeight: 137,
                    borderRadius: "4px",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    fontSize: 12,
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: "#000",
                    p: "12px 56px 12px 12px",
                  },
                }}
              />

              <Box
                onClick={onMicClick}
                sx={{
                  position: "absolute",
                  right: 12,
                  bottom: 8,
                  width: 44,
                  height: 30,
                  borderRadius: "8px",
                  background: isRecording ? "#4E657F" : "#FFFFFF",
                  outline: `1px solid ${isRecording ? "#4E657F" : "#DBE2EB"}`,
                  outlineOffset: "-1px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0px 1px 3px rgba(15, 23, 42, 0.18)",
                  cursor: "pointer",
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/generals/microphone.svg"
                  alt="microphone"
                  sx={{
                    width: 18,
                    height: 18,
                    filter: isRecording ? "invert(1)" : "none",
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function MonitoringCard({
  item,
  deleting,
  onEdit,
  onDelete,
}: Readonly<{
  item: MonitoringRow;
  deleting: boolean;
  onEdit: (item: MonitoringRow) => void;
  onDelete: (id: number) => void;
}>) {
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: 275,
        p: "5px 0",
        background: "#fff",
        boxShadow: "0px 1px 8px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        borderRadius: "8px",
      }}
    >
      <Stack alignItems="center" spacing={1}>
        <Box
          sx={{
            alignSelf: "stretch",
            px: 1.5,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography sx={{ color: "#000", fontSize: 14, fontFamily: "Poppins", fontWeight: 400, lineHeight: "21px" }}>
            {formatDateTime(item.ptime)}
          </Typography>

          <RoleInfo isDoctor={item.isDoctor} user={item.cuser} />

          <Stack direction="row" alignItems="center" spacing={0.25}>
            <ActionIconButton disabled={!item.canEdit} onClick={() => onEdit(item)}>
              <EditOutlinedIcon sx={{ width: 18, height: 18 }} />
            </ActionIconButton>
            <ActionIconButton disabled={!item.canDelete || deleting} onClick={() => onDelete(item.id)}>
              {deleting ? <CircularProgress size={16} sx={{ color: "#687D96" }} /> : <DeleteOutlineRoundedIcon sx={{ width: 18, height: 18 }} />}
            </ActionIconButton>
          </Stack>
        </Box>

        <Box
          sx={{
            width: "calc(100% - 24px)",
            p: 1.5,
            background: "#F1F4F8",
            borderRadius: "8px",
            outline: "1px solid #DBE2EB",
            outlineOffset: "-1px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 1.25,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              color: "#000",
              fontSize: 12,
              fontFamily: "Poppins",
              fontWeight: 400,
              lineHeight: "18px",
              whiteSpace: "pre-line",
              wordBreak: "break-word",
            }}
          >
            {item.note}
          </Typography>

          <Box
            sx={{
              width: 44,
              height: 30,
              background: "#fff",
              borderRadius: "8px",
              outline: "1px solid #DBE2EB",
              outlineOffset: "-1px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4E657F",
              flexShrink: 0,
            }}
          >
            <Box component="img" src="/assets/icons/generals/microphone.svg" alt="microphone" sx={{ width: 18, height: 18 }} />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

export default function MonitoringsPanel({ patientId }: Readonly<MonitoringsPanelProps>) {
  const [items, setItems] = useState<MonitoringRow[]>([]);
  const [note, setNote] = useState("");
  const [editingItem, setEditingItem] = useState<MonitoringRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const shouldKeepRecordingRef = useRef(false);
  const noteRef = useRef("");
  const committedNoteRef = useRef("");

  useEffect(() => {
    noteRef.current = note;
  }, [note]);

  const showAlert = (message: string, type: "success" | "error") => {
    setAlertMessage(message);
    setAlertType(type);
    setAlertOpen(true);
  };

  const normalizeRows = (response: unknown): MonitoringRow[] => {
    const source = Array.isArray(response)
      ? response
      : Array.isArray((response as { data?: unknown[] } | null | undefined)?.data)
        ? (response as { data: unknown[] }).data
        : [];

    return source
      .map((item) => item as PatientMonitoringResponseDto)
      .map((item) => ({
        id: item.id,
        cuser: item.cuser || "",
        note: item.note || "",
        ptime: item.ptime || item.ctime,
        isDoctor: Boolean(item.isdoctor),
        canEdit: Boolean(item.canedit),
        canDelete: Boolean(item.candelete),
      }))
      .sort((a, b) => {
        const timeDiff = parseMonitoringTimestamp(b.ptime) - parseMonitoringTimestamp(a.ptime);
        return timeDiff !== 0 ? timeDiff : b.id - a.id;
      });
  };

  const loadMonitorings = useCallback(async () => {
    if (!patientId) {
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      const response = await referenceService.patient.GetPatientMonitoring({
        PtId: Number(patientId),
        all_recs: 1,
      });

      setItems(normalizeRows(response).slice(0, 3));
    } catch (error) {
      console.error(error);
      showAlert("İzlemler yüklenemedi.", "error");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    setNote("");
    setEditingItem(null);
    loadMonitorings();
  }, [loadMonitorings]);

  const stopRecognition = useCallback(() => {
    shouldKeepRecordingRef.current = false;
    setIsRecording(false);
    try {
      recognitionRef.current?.stop();
    } catch {
    }
  }, []);

  useEffect(() => {
    return () => {
      stopRecognition();
    };
  }, [stopRecognition]);

  const handleMicClick = () => {
    if (isRecording) {
      stopRecognition();
      return;
    }

    const SpeechRecognitionCtor =
      (globalThis as any).SpeechRecognition ||
      (globalThis as any).webkitSpeechRecognition;

    if (!SpeechRecognitionCtor) {
      showAlert("Bu tarayıcıda sesli giriş desteklenmiyor.", "error");
      return;
    }

    try {
      if (!recognitionRef.current) {
        const recognition = new SpeechRecognitionCtor();
        recognition.lang = "tr-TR";
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
          committedNoteRef.current = noteRef.current.trim();
          setIsRecording(true);
        };

        recognition.onresult = (event: any) => {
          const results = event?.results;
          if (!results) return;

          let finalText = "";
          let interimText = "";

          for (let i = event.resultIndex ?? 0; i < results.length; i += 1) {
            const result = results[i];
            const transcript = result?.[0]?.transcript?.toString?.().trim?.() || "";
            if (!transcript) continue;

            if (result?.isFinal) {
              finalText += `${finalText ? " " : ""}${transcript}`;
            } else {
              interimText += `${interimText ? " " : ""}${transcript}`;
            }
          }

          const base = committedNoteRef.current;
          const draftChunk = [finalText, interimText].filter(Boolean).join(" ").trim();
          if (!draftChunk) return;

          const draftValue = base ? `${base}\n${draftChunk}` : draftChunk;
          noteRef.current = draftValue;
          setNote(draftValue);

          if (finalText) {
            committedNoteRef.current = base ? `${base}\n${finalText}` : finalText;
            noteRef.current = committedNoteRef.current;
            setNote(committedNoteRef.current);
          }
        };

        recognition.onerror = () => {
          shouldKeepRecordingRef.current = false;
          setIsRecording(false);
          showAlert("Sesli giriş alınamadı.", "error");
        };

        recognition.onend = () => {
          if (shouldKeepRecordingRef.current) {
            try {
              recognition.start();
              return;
            } catch {
            }
          }

          setNote(noteRef.current || committedNoteRef.current);
          setIsRecording(false);
        };

        recognitionRef.current = recognition as SpeechRecognitionLike;
      }

      shouldKeepRecordingRef.current = true;
      committedNoteRef.current = noteRef.current.trim();
      recognitionRef.current.start();
    } catch {
      setIsRecording(false);
      showAlert("Mikrofon başlatılamadı.", "error");
    }
  };

  const resetDraft = () => {
    stopRecognition();
    setEditingItem(null);
    setNote("");
    noteRef.current = "";
    committedNoteRef.current = "";
  };

  const handleSave = async () => {
    if (!patientId) return;

    if (!note.trim()) {
      showAlert("İzlem notu boş bırakılamaz.", "error");
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await referenceService.patient.UpdatePatientMonitoring(editingItem.id, {
          note: note.trim(),
          write_on_epicrise: "0",
          ptime: toLocalISO(new Date()),
        });
        showAlert("İzlem güncellendi.", "success");
      } else {
        await referenceService.patient.AddPatientMonitoring({
          pt_id: Number(patientId),
          note: note.trim(),
          write_on_epicrise: "0",
          ptime: toLocalISO(new Date()),
        });
        showAlert("İzlem kaydedildi.", "success");
      }

      resetDraft();
      await loadMonitorings();
    } catch (error: any) {
      console.error(error);
      showAlert(error?.response?.data?.error || "İzlem kaydedilemedi.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: MonitoringRow) => {
    if (!item.canEdit) return;
    stopRecognition();
    setEditingItem(item);
    setNote(item.note);
    noteRef.current = item.note;
    committedNoteRef.current = item.note;
  };

  const handleDeleteRequest = (id: number) => {
    const item = items.find((row) => row.id === id);
    if (!item?.canDelete) return;
    setPendingDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return false;

    setDeletingId(pendingDeleteId);
    try {
      await referenceService.patient.DeletePatientMonitoring(pendingDeleteId);
      showAlert("İzlem silindi.", "success");

      if (editingItem?.id === pendingDeleteId) {
        resetDraft();
      }

      await loadMonitorings();
      return true;
    } catch (error: any) {
      console.error(error);
      showAlert(error?.response?.data?.error || "İzlem silinemedi.", "error");
      return false;
    } finally {
      setDeletingId(null);
      setPendingDeleteId(null);
      setDeleteDialogOpen(false);
    }
  };

  const visibleItems = useMemo(
    () => items.filter((item) => item.id !== editingItem?.id).slice(0, 3),
    [editingItem?.id, items]
  );

  return (
    <>
      <Box
        sx={{
          width: "100%",
          p: 3,
          background: "#F7F9FB",
          outline: "1px solid #EAEDF4",
          outlineOffset: "-0.5px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          borderRadius: "0 0 8px 8px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          <MonitoringDraftCard
            note={note}
            saving={saving}
            editing={Boolean(editingItem)}
            isRecording={isRecording}
            onNoteChange={setNote}
            onSave={handleSave}
            onMicClick={handleMicClick}
          />

          {loading ? (
            <Box
              sx={{
                minHeight: 275,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#fff",
                borderRadius: "8px",
                boxShadow: "0px 1px 8px rgba(0, 0, 0, 0.08)",
              }}
            >
              <CircularProgress size={32} sx={{ color: "#2979C3" }} />
            </Box>
          ) : (
            visibleItems.map((item) => (
              <MonitoringCard
                key={item.id}
                item={item}
                deleting={deletingId === item.id}
                onEdit={handleEdit}
                onDelete={handleDeleteRequest}
              />
            ))
          )}
        </Box>
      </Box>

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setPendingDeleteId(null);
        }}
        patientId={pendingDeleteId?.toString() || ""}
        title="Silme Onayı"
        content="Bu izlemi silmek istediğinize emin misiniz?"
        onConfirm={handleDeleteConfirm}
        successMessage="İzlem silindi."
        errorMessage="İzlem silinemedi."
        hideSnackbar
      />

      <ByzMuiSnackBar
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        title={alertType === "success" ? "Başarılı" : "Hata"}
        message={alertMessage}
        type={alertType}
      />
    </>
  );
}
